import { useState } from "react";
import { X, DownloadSimple, FilePdf, Eye, ArrowLeft, ArrowRight, PencilSimple, Check } from "@phosphor-icons/react";

const isPdf = (att) => {
  if (!att) return false;
  const ct = att.content_type || att.contentType || "";
  const fn = (att.filename || att.path || "").toLowerCase();
  return ct === "application/pdf" || fn.endsWith(".pdf");
};

const getAttUrl = (att, apiBase) => {
  const path = (att?.path || att || "").toString().replace("quotations/attachments/", "");
  return `${apiBase}/quotations/attachments/${path}`;
};

const AttachmentGrid = ({ attachments, apiBase, onRemove, onUpdateLabel, editable = false }) => {
  const [lightbox, setLightbox] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");

  if (!attachments || attachments.length === 0) return null;

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const goPrev = () => setLightbox((p) => (p > 0 ? p - 1 : attachments.length - 1));
  const goNext = () => setLightbox((p) => (p < attachments.length - 1 ? p + 1 : 0));

  const handleDownload = (att) => {
    const url = getAttUrl(att, apiBase);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename || "archivo";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const startEditLabel = (idx, currentLabel) => {
    setEditingLabel(idx);
    setLabelDraft(currentLabel || "");
  };

  const saveLabel = (idx) => {
    if (onUpdateLabel) onUpdateLabel(idx, labelDraft);
    setEditingLabel(null);
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="attachments-grid">
        {attachments.map((att, idx) => {
          const pdf = isPdf(att);
          const url = getAttUrl(att, apiBase);
          const name = att?.filename || `Adjunto ${idx + 1}`;
          const label = att?.label || "";

          return (
            <div
              key={idx}
              className="relative group border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-white"
              data-testid={`attachment-item-${idx}`}
            >
              {pdf ? (
                <div
                  className="w-full h-36 flex flex-col items-center justify-center bg-red-50 gap-1"
                  onClick={() => window.open(url, "_blank")}
                >
                  <FilePdf size={36} className="text-red-500" weight="duotone" />
                  {label && (
                    <span className="text-xs font-bold text-red-800 bg-red-100 px-2 py-0.5 rounded">
                      {label}
                    </span>
                  )}
                  <span className="text-[10px] text-red-600 px-2 text-center truncate max-w-full">
                    {name}
                  </span>
                </div>
              ) : (
                <img
                  src={url}
                  alt={name}
                  className="w-full h-36 object-cover"
                  onClick={() => openLightbox(idx)}
                />
              )}

              {/* Overlay actions */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                {pdf ? (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); window.open(url, "_blank"); }}
                      className="p-2 bg-white/90 rounded-full text-blue-600 hover:bg-white shadow"
                      title="Ver PDF"
                      data-testid={`view-pdf-${idx}`}
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(att); }}
                      className="p-2 bg-white/90 rounded-full text-green-600 hover:bg-white shadow"
                      title="Descargar"
                      data-testid={`download-att-${idx}`}
                    >
                      <DownloadSimple size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); openLightbox(idx); }}
                      className="p-2 bg-white/90 rounded-full text-blue-600 hover:bg-white shadow"
                      title="Ver imagen"
                      data-testid={`view-img-${idx}`}
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(att); }}
                      className="p-2 bg-white/90 rounded-full text-green-600 hover:bg-white shadow"
                      title="Descargar"
                      data-testid={`download-att-${idx}`}
                    >
                      <DownloadSimple size={18} />
                    </button>
                  </>
                )}
                {editable && onRemove && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
                    className="p-2 bg-red-600 rounded-full text-white hover:bg-red-700 shadow"
                    title="Eliminar"
                    data-testid={`remove-att-${idx}`}
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              {/* Label editor / display for PDFs */}
              {pdf && editable ? (
                <div className="px-2 py-1.5 bg-white border-t">
                  {editingLabel === idx ? (
                    <div className="flex items-center gap-1">
                      <input
                        value={labelDraft}
                        onChange={(e) => setLabelDraft(e.target.value)}
                        placeholder="Ej: CATALOGO"
                        className="flex-1 text-xs border rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        onKeyDown={(e) => e.key === "Enter" && saveLabel(idx)}
                        autoFocus
                        data-testid={`label-input-${idx}`}
                      />
                      <button
                        onClick={() => saveLabel(idx)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        data-testid={`label-save-${idx}`}
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-600 truncate flex-1">
                        {label || <span className="text-slate-400 italic">Sin titulo</span>}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); startEditLabel(idx, label); }}
                        className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Editar titulo para PDF"
                        data-testid={`label-edit-${idx}`}
                      >
                        <PencilSimple size={12} />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500 px-2 py-1.5 truncate bg-white border-t">
                  {pdf && label ? <span className="font-semibold text-red-700">{label} - </span> : null}
                  {name}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Lightbox Modal */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={closeLightbox}
          tabIndex={0}
          data-testid="attachment-lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
            onClick={closeLightbox}
            data-testid="lightbox-close"
          >
            <X size={24} />
          </button>

          <button
            className="absolute top-4 right-16 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
            onClick={(e) => { e.stopPropagation(); handleDownload(attachments[lightbox]); }}
            data-testid="lightbox-download"
          >
            <DownloadSimple size={24} />
          </button>

          {attachments.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                data-testid="lightbox-prev"
              >
                <ArrowLeft size={24} />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                data-testid="lightbox-next"
              >
                <ArrowRight size={24} />
              </button>
            </>
          )}

          <div className="max-w-5xl max-h-[85vh] mx-4" onClick={(e) => e.stopPropagation()}>
            {isPdf(attachments[lightbox]) ? (
              <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-4">
                <FilePdf size={64} className="text-red-500" weight="duotone" />
                <p className="text-lg font-semibold text-slate-800">
                  {attachments[lightbox]?.label || attachments[lightbox]?.filename || "Documento PDF"}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => window.open(getAttUrl(attachments[lightbox], apiBase), "_blank")}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
                    data-testid="lightbox-open-pdf"
                  >
                    <Eye size={18} /> Abrir PDF
                  </button>
                  <button
                    onClick={() => handleDownload(attachments[lightbox])}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-2 text-sm font-medium"
                    data-testid="lightbox-download-pdf"
                  >
                    <DownloadSimple size={18} /> Descargar
                  </button>
                </div>
              </div>
            ) : (
              <img
                src={getAttUrl(attachments[lightbox], apiBase)}
                alt={attachments[lightbox]?.filename || ""}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            )}
          </div>

          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {lightbox + 1} / {attachments.length}
          </p>
        </div>
      )}
    </>
  );
};

export { AttachmentGrid, isPdf, getAttUrl };
